import React, {useState, useRef, useEffect, useCallback, Fragment} from 'react';
import {Button} from 'ui-kits';
import Circle from './Circle';
import {StyleSheet} from 'react-native';

type PathPoint = {
  x: number;
  y: number;
};

type Entity = {
  name: string;
  path: PathPoint[];
  color: string;
  size: number;
  blur?: number;
  opacity?: number;
  zIndex?: number;
};

type AnimatedCircleProps = {
  entities: Entity[];
  duration: number;
  debug?: boolean;
  circleScale?: number;
};

function* createCycler(
  array: PathPoint[],
): Generator<PathPoint, never, unknown> {
  let index = 0;
  while (true) {
    yield array[index];
    index = (index + 1) % array.length;
  }
}

export const AnimatedCircle: React.FC<AnimatedCircleProps> = ({
  entities,
  duration,
  debug = false,
  circleScale = 1,
}) => {
  // Create a cycler for each entity's path and store it in a ref
  const cyclers = useRef(
    entities.map(entity => createCycler(entity.path)),
  ).current;

  // Store positions in state to trigger re-renders
  const [positions, setPositions] = useState<PathPoint[]>(
    entities.map(entity => entity.path[entity.path.length - 1]), // Start at the last point in the path
  );

  // Handle the next step for all entities
  const handleNext = useCallback(() => {
    setPositions(prevPositions => {
      return prevPositions.map((_, index) => {
        // Get the next value from each cycler
        const newValue = cyclers[index].next().value as PathPoint;
        return newValue;
      });
    });
  }, [cyclers]);

  // Automatically cycle the positions
  useEffect(() => {
    if (!debug) {
      const interval = setInterval(() => {
        handleNext(); // Cycle positions at the given interval
      }, duration);

      return () => clearInterval(interval); // Clear the interval on cleanup
    }
  }, [handleNext, duration, debug]);

  return (
    <Fragment>
      {entities.map((entity, index) => {
        return (
          <Circle
            key={entity.name}
            blurAmount={entity?.blur ?? 40}
            // blurAmount={0}
            color={entity.color}
            size={entity.size * circleScale}
            x={positions[index].x}
            y={positions[index].y}
            opacity={entity?.opacity || 1}
            zIndex={entity?.zIndex || -1}
          />
        );
      })}
      {debug ? (
        <Button
          text="Cycle All"
          onPress={handleNext}
          style={styles.debugButton}
        />
      ) : null}
    </Fragment>
  );
};

const styles = StyleSheet.create({
  debugButton: {
    top: '101%',
    width: '100%',
    position: 'absolute',
  },
});
